/* Bringing a block of the case page into view — the payments list «Разнести»
 * points at (S-12), the legs and the invoicing an old `?section=` alias lands on
 * (S-11). One helper for all of them, moved here from `MoneySection` when the
 * page needed it too.
 *
 * NEVER `scrollIntoView`. It scrolls every scrollable ancestor, the document
 * included, and in Chrome it dragged the whole app shell — top bar and all — up
 * with it (see `features/quotes/useSectionSpy.ts` and the wiki's failure
 * modes). jsdom stubs it for every test (`src/test/setup.ts`), so a test cannot
 * catch it on its own: `reveal.test.ts` asserts it is not called. */

/** The nearest scrolling ancestor — AppShell's `<main>` on a case page. */
export function scrollParent(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement
  while (node) {
    const { overflowY } = window.getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
    node = node.parentElement
  }
  return null
}

/** Scrolls ONLY the shell's scroll container to `element`, below the case's
 *  sticky header, then moves focus there. The element is focusable without
 *  being in the tab order (`tabIndex={-1}`). */
export function reveal(element: HTMLElement) {
  const container = scrollParent(element)
  if (container && typeof container.scrollTo === 'function') {
    const sticky = container.querySelector<HTMLElement>('.sticky')
    const top =
      element.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      (sticky?.getBoundingClientRect().height ?? 0) -
      12
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    container.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' })
  }
  // `preventScroll`: the scroll above is on its way, and a focus scroll would
  // cut it short — or scroll the document itself.
  element.focus({ preventScroll: true })
}

/** Breathing room left past the row's edge, so a revealed tab does not sit
 *  flush against the card's border. */
const ROW_EDGE = 8

/** Brings `item` into the visible span of a horizontally scrolling `row` by
 *  moving the ROW's `scrollLeft` alone — the phone's six-tab switcher (S-11
 *  revision). Nothing else scrolls: not the document, not the shell's
 *  container, and never through `scrollIntoView` (see the note at the top).
 *  An item already in view, or a row that does not scroll (the desktop rail,
 *  `overflow: visible`), moves nothing. */
export function revealInRow(row: HTMLElement, item: HTMLElement) {
  const bounds = row.getBoundingClientRect()
  const box = item.getBoundingClientRect()
  if (box.right > bounds.right) {
    row.scrollLeft += box.right - bounds.right + ROW_EDGE
  } else if (box.left < bounds.left) {
    row.scrollLeft -= bounds.left - box.left + ROW_EDGE
  }
}
